import React, { JSX, useEffect, useState } from "react";

import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import { DATE_FORMATTER_MONTH_DAY_ONLY, DATE_FORMATTER_SECONDS } from "utils/constants";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { useGetDataInfoRange, useGetRecentData } from "api/api-hooks/datasets-hooks";
import { Dialog } from "primereact/dialog";
import { Instant, IsoFields, LocalDate, ZonedDateTime, ZoneOffset } from "@js-joda/core";
import useTimeState from "hooks/dashboard/useTimeState";
import { safeIdString } from "utils/utils";
import { Tooltip } from "primereact/tooltip";
import { I, T } from "services/lang/LanguageService";
import {
  checkIfDataInLastFinishedMonthExists,
  checkIfDataInLastFinishedWeekExists,
  DweenTimeUnitEnum,
  fmtDatetime,
  getMonthValuesForYears,
  toInstant,
  toZonedDateTime,
} from "utils/time-utils";
import { Timezones, useTimezone } from "hooks/useTimezone";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import { DataDisplayType, DiffType, DiffValueOptions, ValueChangeIndicatorProps } from "./ValueChangeIndicatorTypes";
import {
  daysArray,
  defaultFromDateTimeInstant,
  defaultToDateTimeInstant,
  formatNumber,
  getLastFinishedWeek,
  getTextColor,
  monthsArray,
} from "../charts/utils";
import HorizontalBarChart from "./HorizontalBarChart";
import {
  formatDiff,
  getArrowDirection,
  getDiffClass,
  getDiffClassName,
  getMainClassName,
  getMonthYearComparison,
  getNoDataMessageWeek,
  getTitleClassName,
  getPeriodLabel,
} from "./ValueChangeIndicatorUtils";
import "./ValueChangeIndicator.scss";
import { defaultLoadConf } from "../data-input-output-table/defaultConfigs";
import ValueContainer from "./ValueContainer";
import getCurrentDate from "../current-date-display/CurrentDateDisplayUtils";
import DiffValueDisplayer from "./DiffValueDisplayer";

export default function ValueChangeIndicator(props: ValueChangeIndicatorProps) {
  const tz = useTimezone();

  const setStatus = useComponentStateCallback(props);

  const [dialog, setDialog] = useState(false);
  const [currentDiffValueOptions, setCurrentDiffValueOptions] = useState<DiffValueOptions>({
    value: 0,
    currentValueInfo: "",
    previousValueInfo: "",
    isHorizontalBarChartDiffLoaded: false,
  });

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const defaultFromDate =
    timeState.from ??
    defaultFromDateTimeInstant({
      zoneId: tz.company,
      loadConf: { ...defaultLoadConf, ...props.loadConf },
      referenceTime: props.instantReferenceTime,
    });

  const defaultToDate =
    timeState.to ??
    defaultToDateTimeInstant({
      zoneId: tz.company,
      loadConf: {
        ...defaultLoadConf,
        ...props.loadConf,
        alignUnit: props.useDataUpToLastFinishedMonth ? DweenTimeUnitEnum.MONTHS : props.loadConf?.alignUnit,
        leadUnit: props.useDataUpToLastFinishedMonth ? DweenTimeUnitEnum.MINUTES : props.loadConf?.leadUnit,
        lead: props.useDataUpToLastFinishedMonth ? 0 : props.loadConf?.lead,
      },
      referenceTime: props.instantReferenceTime,
      minutesToAdd: props.useDataUpToLastFinishedMonth ? 0 : -1,
    });

  let refTime: string = defaultToDate.toString();
  if (props.useDataUpToLastFinishedMonth && defaultToDate) {
    const zdt = ZonedDateTime.ofInstant(toInstant(defaultToDate), ZoneOffset.UTC);
    refTime = zdt.minusMinutes(2).toInstant().toString();
  }

  const { data, isLoading, error } = useGetRecentData(props.datasets, {
    count: 2,
    refTime,
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled: props.dataDisplayType === DataDisplayType.LAST_VALUE || props.dataDisplayType === undefined,
    },
  });

  const {
    data: dataRange,
    isLoading: isLoadingRange,
    error: errorRange,
  } = useGetDataInfoRange(
    props.datasets,
    {
      timeStart: defaultFromDate.toString(),
      timeEnd: defaultToDate.toString(),
    },
    {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled:
        props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ||
        props.dataDisplayType === DataDisplayType.AVERAGE_VALUE ||
        props.dataDisplayType === DataDisplayType.MONTH_YEAR_COMPARISON ||
        props.dataDisplayType === DataDisplayType.WEEK_YEAR_COMPARISON,
    }
  );

  const decimalPlaces = props.decimalPlaces || data?.[0]?.decimalPlaces;
  const diffDecimalPlaces = props.diffValueDecimalPlaces ?? decimalPlaces;
  const currentDataset = data?.[0] ?? dataRange?.[0];
  const currentData = currentDataset?.data;

  useEffect(() => {
    if (data && currentData && currentData.length >= 2 && props.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART) {
      const newDiff: DiffValueOptions = {
        value: (currentData[1]?.value ?? 0) - (currentData[0]?.value ?? 0),
        currentValueInfo: renderDiffRow(
          "Current",
          currentData[1]?.value ?? 0,
          currentDataset?.unit ?? "",
          currentData[1]?.key ?? "",
          tz
        ),
        previousValueInfo: renderDiffRow(
          "Previous",
          currentData[0]?.value ?? 0,
          currentDataset?.unit ?? "",
          currentData[0]?.key ?? "",
          tz
        ),
      };
      setCurrentDiffValueOptions(newDiff);
    }
  }, [data, currentData]);

  useEffect(() => {
    if (props.reportMode && ((!isLoading && data !== undefined) || (!isLoadingRange && dataRange !== undefined))) {
      if (
        props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART &&
        !currentDiffValueOptions.isHorizontalBarChartDiffLoaded
      ) {
        return undefined;
      }
      return setStatus({ state: "finished" });
    }

    return undefined;
  }, [isLoading, isLoadingRange, currentDiffValueOptions]);

  if (error || errorRange) {
    const message = error?.message ?? errorRange?.message;
    easasAlert({
      summary: T("alert.failed-to-fetch-data"),
      detail: message,
      severity: "error",
    });
    setStatus({ state: "error", error: message });
    return null;
  }

  const renderDiffRow = (label: string, value: number, unit: string, timestamp: string, currentTz: Timezones): JSX.Element => (
    <div className="flex flex-row justify-content-between gap-2">
      <b>{label}:</b>
      <span>
        {value.toFixed(diffDecimalPlaces)} {unit}, {fmtDatetime(timestamp, currentTz.display, DATE_FORMATTER_SECONDS)}
      </span>
    </div>
  );

  const getReferenceWeek = (useLastFinished: boolean) => {
    const refZdt = ZonedDateTime.now(tz.company);
    let refWeek = refZdt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);

    if (useLastFinished) {
      refWeek = getLastFinishedWeek(refWeek, refZdt.year(), refZdt).refWeek;
    }
    return refWeek;
  };

  const getWeekYearComparison = (dataset: DatasetDataInfoResponse, useLastFinished: boolean) => {
    if (!dataset?.data) return null;

    const refWeek = getReferenceWeek(useLastFinished);
    const refYear = ZonedDateTime.now(tz.company).year();

    const groupedByYear = new Map<number, number>();

    dataset.data.forEach(point => {
      const zdt = toZonedDateTime(point.key, tz.company);
      const week = zdt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
      const year = zdt.year();

      if (week === refWeek) {
        groupedByYear.set(year, point.value ?? 0);
      }
    });

    const current = groupedByYear.get(refYear);
    const comparisons = Array.from(groupedByYear.entries())
      .filter(([year]) => year !== refYear)
      .map(([year, value]) => ({ year, value }));
    return { current, comparisons, refWeek, refYear };
  };

  const renderDatasetValue = (dataset: DatasetDataInfoResponse) => {
    const safeId = safeIdString(dataset.identifier);
    const tooltipId = `last-value-${safeId}-tooltip`;
    const val = dataset.data?.[1] ?? dataset.data?.[0];
    const isMultiple = props.datasets.length > 1 && !props.displayInline;
    const textColor = props.highlightValue ? "" : getTextColor(props.theme);
    const datasetValueClassName = `value-container${isMultiple ? "-multiple" : ""} flex flex-row gap-1 align-items-end ${props.highlightValue ? "highlight-value" : ""}`;

    if (props.hideZeroValues && val?.value === 0) {
      return null;
    }

    return (
      <React.Fragment key={dataset.identifier}>
        <div
          id={tooltipId}
          style={{ color: textColor }}
          className={datasetValueClassName}
          onDoubleClick={() => setDialog(true)}
        >
          <div className="value cursor-pointer font-bold">
            {val.value && formatNumber(val.value, decimalPlaces, props.convertToAbsoluteValues)}
          </div>
          <div className="unit cursor-pointer font-bold">{dataset.unit}</div>
        </div>

        <Tooltip target={`#${tooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
          {val ? (
            <div>{val.value && renderDiffRow(T("word.current-value"), val.value, dataset.unit, val.key, tz)}</div>
          ) : (
            T("word.no-data")
          )}
        </Tooltip>
      </React.Fragment>
    );
  };

  const getDiffTooltip = (prevInfo?: JSX.Element, currInfo?: JSX.Element) => {
    if (props.dataDisplayType === DataDisplayType.MONTH_YEAR_COMPARISON) {
      return (
        <div className="flex flex-column gap-1">
          <div>{prevInfo}</div>
          <div>{currInfo}</div>
        </div>
      );
    }
    if (props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART) {
      return (
        <div className="flex flex-column gap-1">
          <div>{currentDiffValueOptions.previousValueInfo}</div>
          <div>{currentDiffValueOptions.currentValueInfo}</div>
        </div>
      );
    }
    if (currentData?.[0] && currentData?.[1] && currentDataset) {
      return (
        <div className="flex flex-column gap-1">
          {currentData[0].value &&
            renderDiffRow(T("word.previous-value"), currentData[0].value, currentDataset.unit, currentData[0].key, tz)}
          {currentData[1].value &&
            renderDiffRow(T("word.current-value"), currentData[1].value, currentDataset.unit, currentData[1].key, tz)}
        </div>
      );
    }
    return <div>{T("word.no-data")}</div>;
  };

  const getComponentWidth = () => {
    if (props.reportMode) {
      return `${props.width}px` || "100%";
    }
    return "100%";
  };

  const getItemsForAverageValues = (currentDataRange: DatasetDataInfoResponse[]) =>
    currentDataRange.map(ds => {
      const values = (ds.data ?? []).map(p => p?.value).filter((v): v is number => Number.isFinite(v));
      const count = values.length;
      const avg = count ? values.reduce((a, b) => a + b, 0) / count : null;
      const dataArray = ds.data ?? [];
      return { ds, avg, count, dataArray };
    });

  const getFromDate = (startOfTheProject: Instant): string => {
    const currentStartOfTheProject = toZonedDateTime(startOfTheProject, tz.company);

    return currentStartOfTheProject.format(DATE_FORMATTER_MONTH_DAY_ONLY);
  };

  const calculateFromStartOfTheProject = (): boolean => {
    if (!props.startOfTheProject) return false;

    const startInstant = toInstant(props.startOfTheProject);
    const today = LocalDate.now(tz.company);
    const projectStart = startInstant.atZone(tz.company).toLocalDate();

    const defaultFromInstant = toInstant(defaultFromDate);
    const defaultFrom = defaultFromInstant.atZone(tz.company).toLocalDate();

    return (
      !!props.useStartOfTheProject &&
      projectStart.isAfter(defaultFrom) &&
      (projectStart.isBefore(today) || projectStart.equals(today))
    );
  };

  const titleAndSubtitleTemplate = () => {
    return (
      <div className="flex flex-column title-container">
        <div
          style={{ color: getTextColor(props.theme) }}
          className={getTitleClassName(props.dataDisplayType, props.displayInline)}
        >
          <div>{I(props.title ?? "")}</div>
        </div>
        <div style={{ color: getTextColor(props.theme) }} className="subtitle font-medium">
          {(props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ||
            (props.dataDisplayType === DataDisplayType.WEEK_YEAR_COMPARISON && props.useHorizontalBarChart)) &&
          props.upToUnit ? (
            <div>
              {calculateFromStartOfTheProject() && props.startOfTheProject ? (
                `${T("word.from")} ${props.dataDisplayType === DataDisplayType.WEEK_YEAR_COMPARISON ? getPeriodLabel(props.upToUnit, props.startOfTheProject, tz.company) : getFromDate(props.startOfTheProject)},
                  
                  ${T("label.up-to").toLocaleLowerCase()}
                  ${getPeriodLabel(props.upToUnit, props.instantReferenceTime, tz.company, props.useDataUpToLastFinishedWeek)}`
              ) : (
                <>
                  {T("label.up-to")} {getPeriodLabel(props.upToUnit, props.instantReferenceTime, tz.company, props.useDataUpToLastFinishedWeek)}
                </>
              )}
            </div>
          ) : (
            <div>{I(props.subtitle ?? "")}</div>
          )}
        </div>
      </div>
    );
  };

  const diffValueForWeekYearComparison = () => {
    if (props.dataDisplayType !== DataDisplayType.WEEK_YEAR_COMPARISON || !dataRange) return null;
    const dataset = dataRange[0];
    const safeDiffId = safeIdString(dataset.identifier);
    const diffTooltipId = `week-year-diff-${safeDiffId}-tooltip`;

    if (props.useHorizontalBarChart) {
      if (!currentDiffValueOptions.isHorizontalBarChartDiffLoaded) return null;

      const diffValue = currentDiffValueOptions.value ?? 0;
      const isNegative = diffValue < 0;
      const isNeutral = Number(diffValue.toFixed(props.diffValueDecimalPlaces)) === 0;
      const diffClass = getDiffClass(isNegative);
      return (
        <>
          <div id={diffTooltipId} className={getDiffClassName(isNeutral, props.dataDisplayType, props.useDiffIndicatorColors)}>
            <div className={diffClass}>
              {props.diffType === DiffType.PERCENTAGE
                ? `${diffValue.toFixed(props.diffValueDecimalPlaces)}%`
                : `${diffValue.toFixed(props.diffValueDecimalPlaces)} ${dataRange[0].unit}`}
            </div>
            {isNeutral && <i className="pi pi-arrows-v" />}
            {!isNeutral && <i className={`pi pi-arrow-${getArrowDirection(isNegative)} ${diffClass}`} />}
          </div>

          <Tooltip target={`#${diffTooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
            <div className="flex flex-column gap-1">
              <div>{currentDiffValueOptions.previousValueInfo}</div>
              <div>{currentDiffValueOptions.currentValueInfo}</div>
            </div>
          </Tooltip>
        </>
      );
    }
    const comparison = getWeekYearComparison(dataset, props.useDataUpToLastFinishedWeek ?? false);

    if (!comparison || comparison.current == null || comparison.comparisons.length === 0) {
      return null;
    }

    const prev = comparison.comparisons[0].value;
    const curr = comparison.current;

    if (props.useDataUpToLastFinishedMonth && dataset.data[1].key) {
      const isInLastMonth = checkIfDataInLastFinishedMonthExists(dataset.data[1].key, tz.company);
      if (!isInLastMonth) return null;
    }

    const findPoint = (year: number) =>
      dataset.data.find(point => {
        const zdt = Instant.parse(point.key).atZone(tz.company);
        return zdt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR) === comparison.refWeek && zdt.year() === year;
      });

    const prevPoint = findPoint(comparison.comparisons[0].year);
    const currPoint = findPoint(comparison.refYear);

    const prevYear = prevPoint && Instant.parse(prevPoint.key).atZone(tz.company).year();
    const currYear = currPoint && Instant.parse(currPoint.key).atZone(tz.company).year();

    const diffValue = curr - prev;
    const roundedDiff = Number(diffValue.toFixed(props.diffValueDecimalPlaces));

    const isNegative = diffValue < 0;
    const isNeutral = roundedDiff === 0;

    const week = getCurrentDate(tz.display, true, props.useDataUpToLastFinishedWeek);

    const diffLabel =
      props.diffType === DiffType.PERCENTAGE
        ? `${(((curr - prev) / prev) * 100).toFixed(props.diffValueDecimalPlaces)}%`
        : `${diffValue.toFixed(props.diffValueDecimalPlaces)} ${dataset.unit}`;
    const diffClass = getDiffClass(isNegative);
    return (
      <>
        <div id={diffTooltipId} className={getDiffClassName(isNeutral, props.dataDisplayType, props.useDiffIndicatorColors)}>
          <div className={diffClass}>{diffLabel}</div>
          {isNeutral && <i className="pi pi-arrows-v" />}
          {!isNeutral && <i className={`pi pi-arrow-${getArrowDirection(isNegative)} ${diffClass}`} />}
        </div>

        <Tooltip target={`#${diffTooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
          <div className="flex flex-column gap-1">
            <div>
              <b>{T("word.previous-value")}:</b> {prev.toFixed(props.diffValueDecimalPlaces)} {dataset.unit}
              {prevPoint && (
                <span className="ml-2">
                  {prevYear} {week}
                </span>
              )}
            </div>
            <div>
              <b>{T("word.current-value")}:</b> {curr.toFixed(props.diffValueDecimalPlaces)} {dataset.unit}
              {currPoint && (
                <span className="ml-2">
                  {currYear} {week}
                </span>
              )}
            </div>
          </div>
        </Tooltip>
      </>
    );
  };

  const horizonatlBarChartTemplate = () => {
    if (!dataRange || !props.aggregationPeriod) return null;
    return (
      <div onDoubleClick={() => setDialog(true)}>
        <HorizontalBarChart
          data={dataRange[0]}
          diffType={props.diffType}
          setCurrentDiffValueOptions={setCurrentDiffValueOptions}
          theme={props.theme}
          reportMode={props.reportMode}
          decimalPlaces={decimalPlaces}
          calculateFromStartOfTheProject
          startOfTheProject={props.startOfTheProject}
          useWeekYearComparison={props.dataDisplayType === DataDisplayType.WEEK_YEAR_COMPARISON}
        />
      </div>
    );
  };

  const diffValueForLastValue = () => {
    if (
      props.displayLastValueDiff &&
      props.dataDisplayType !== DataDisplayType.AVERAGE_VALUE &&
      props.dataDisplayType !== DataDisplayType.MONTH_YEAR_COMPARISON &&
      props.dataDisplayType !== DataDisplayType.WEEK_YEAR_COMPARISON &&
      currentData
    ) {
      const prev = currentData[0]?.value ?? 0;
      const curr = currentData[1]?.value ?? 0;

      if (props.useDataUpToLastFinishedMonth && currentData[1].key) {
        const isInLastMonth = checkIfDataInLastFinishedMonthExists(currentData[1].key, tz.company);
        if (!isInLastMonth) return null;
      }

      const diffValue = currentDiffValueOptions.value ?? curr - prev;
      const safeDiffId = data?.[0]?.identifier?.replace(/[^a-zA-Z0-9-_]/g, "_") ?? "default";
      const diffTooltipId = `last-value-diff-${safeDiffId}-tooltip`;

      return (
        <>
          <DiffValueDisplayer
            dataDisplayType={props.dataDisplayType}
            useDiffIndicatorColors={props.useDiffIndicatorColors}
            diffValue={diffValue}
            diffValueDecimalPlaces={props.diffValueDecimalPlaces}
            formattedValue={formatDiff(
              prev,
              curr,
              currentDataset.unit,
              currentDiffValueOptions.value,
              false,
              props.diffType,
              diffDecimalPlaces,
              props.dataDisplayType
            )}
          />
          <Tooltip target={`#${diffTooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
            {getDiffTooltip()}
          </Tooltip>
        </>
      );
    }
    return null;
  };

  const diffValueForMonthYearComparison = () => {
    if (props.dataDisplayType === DataDisplayType.MONTH_YEAR_COMPARISON && dataRange) {
      const dataset = dataRange[0]?.data ?? [];
      const dataToUse = getMonthValuesForYears(dataset, tz.company, props.useDataUpToLastFinishedMonth);

      let prev: number | null;
      let curr: number | null;
      if (props.useDataUpToLastFinishedMonth) {
        if (dataToUse[1]?.key) {
          const isInLastMonth = checkIfDataInLastFinishedMonthExists(dataToUse[1].key, tz.company);
          if (!isInLastMonth) return null;
        }
        prev = getMonthYearComparison(dataset, true, tz.company).prev;
        curr = getMonthYearComparison(dataset, true, tz.company).curr;
      } else {
        prev = getMonthYearComparison(dataset, false, tz.company).prev;
        curr = getMonthYearComparison(dataset, false, tz.company).curr;
      }
      if (curr == null || prev == null) return null;

      const diffValue = currentDiffValueOptions.value || (curr ?? 0) - (prev ?? 0);
      return (
        <DiffValueDisplayer
          dataDisplayType={props.dataDisplayType}
          useDiffIndicatorColors={props.useDiffIndicatorColors}
          diffValue={diffValue}
          diffValueDecimalPlaces={props.diffValueDecimalPlaces}
          formattedValue={formatDiff(
            prev,
            curr,
            currentDataset?.unit,
            currentDiffValueOptions.value,
            false,
            props.diffType,
            diffDecimalPlaces,
            props.dataDisplayType
          )}
        />
      );
    }
    return null;
  };

  const noDataInLastFinishedMonthTemplate = () => {
    const lastMonth = ZonedDateTime.now(tz.company).minusMonths(1);
    const monthIndex = lastMonth.month().value() - 1;

    const monthName = monthsArray[monthIndex];

    return (
      <div style={{ color: getTextColor(props.theme), fontWeight: "bold" }}>{`${T("label.no-data-in")} ${monthName}`}</div>
    );
  };

  const noDataInLastFinishedWeekTemplate = () => {
    const lastWeek = ZonedDateTime.now(tz.company).minusWeeks(1);
    const weekIndex = lastWeek.dayOfWeek().value() - 1;

    const weekName = daysArray[weekIndex];

    return <div style={{ color: getTextColor(props.theme), fontWeight: "bold" }}>{`${T("label.no-data-in")} ${weekName}`}</div>;
  };

  const lastValueTemplate = () => {
    if (
      data &&
      data.length === 1 &&
      (props.dataDisplayType === DataDisplayType.LAST_VALUE || props.dataDisplayType === undefined)
    ) {
      const dataset = data[0];
      const val = dataset.data?.[1];
      const safeId = safeIdString(dataset.identifier);
      const tooltipId = `last-value-${safeId}-tooltip`;

      if (props.useDataUpToLastFinishedMonth && val?.key) {
        const isInLastMonth = checkIfDataInLastFinishedMonthExists(val.key, tz.company);
        if (!isInLastMonth) return noDataInLastFinishedMonthTemplate();
      }

      return (
        <ValueContainer
          id={tooltipId}
          value={val?.value !== undefined ? formatNumber(val.value, decimalPlaces, props.convertToAbsoluteValues) : null}
          unit={!props.hideUnit ? dataset.unit : undefined}
          highlight={props.highlightValue}
          color={getTextColor(props.theme)}
          onDoubleClick={() => setDialog(true)}
        >
          {val?.value && currValueTooltip(tooltipId, val.value, val.key, dataset.unit)}
        </ValueContainer>
      );
    }
    return null;
  };

  const currValueTooltip = (tooltipId: string, value: number, valueKey: string, unit: string) => {
    return (
      <Tooltip target={`#${tooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
        {value ? <div>{renderDiffRow(T("word.current-value"), value, unit, valueKey, tz)}</div> : T("word.no-data")}
      </Tooltip>
    );
  };

  const lastValueTemplateForMonthYearComparison = () => {
    if (Array.isArray(dataRange) && dataRange.length === 1 && props.dataDisplayType === DataDisplayType.MONTH_YEAR_COMPARISON) {
      const dataset = dataRange[0];
      let value: number;
      let valueKey: string;
      if (props.useDataUpToLastFinishedMonth) {
        const { curr, currValueKey } = getMonthYearComparison(dataset.data, true, tz.company);
        value = curr !== null ? curr : 0;
        valueKey = currValueKey;
      } else {
        const { curr, currValueKey } = getMonthYearComparison(dataset.data, false, tz.company);
        value = curr !== null ? curr : 0;
        valueKey = currValueKey;
      }
      const safeId = safeIdString(dataset.identifier);
      const tooltipId = `last-value-${safeId}-tooltip`;
      return (
        <ValueContainer
          id={tooltipId}
          value={formatNumber(value, decimalPlaces, props.convertToAbsoluteValues)}
          unit={!props.hideUnit ? dataset.unit : undefined}
          highlight={props.highlightValue}
          color={getTextColor(props.theme)}
          onDoubleClick={() => setDialog(true)}
        >
          {currValueTooltip(tooltipId, value, valueKey, dataset.unit)}
        </ValueContainer>
      );
    }
    return null;
  };

  const lastValueTemplateForAverage = () => {
    if (Array.isArray(dataRange) && dataRange.length > 0 && props.dataDisplayType === DataDisplayType.AVERAGE_VALUE) {
      const allUnits = new Set(dataRange.map(ds => ds.unit).filter(Boolean));
      const sameUnit = allUnits.size === 1 ? [...allUnits][0] : undefined;

      return (
        <div
          className={`avg-values-container flex flex-column gap-2 ${props.highlightValue ? "highlight-value" : ""}`}
          style={{ color: props.highlightValue ? "" : getTextColor(props.theme) }}
          onDoubleClick={() => setDialog(true)}
        >
          {getItemsForAverageValues(dataRange).map(({ ds, avg, count, dataArray }) => {
            const dp = decimalPlaces ?? ds.decimalPlaces;
            const unitToShow = sameUnit ?? ds.unit;
            const tooltipId = `avg-value-tooltip`;
            const first = dataArray?.[0];
            const last = dataArray?.[dataArray.length - 1];
            return (
              <ValueContainer
                key={ds.identifier}
                id={tooltipId}
                value={avg !== null ? formatNumber(avg, dp, props.convertToAbsoluteValues) : "–"}
                unit={!props.hideUnit ? unitToShow : undefined}
                color={getTextColor(props.theme)}
              >
                <Tooltip target={`#${tooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
                  {count > 0 ? (
                    <div className="flex flex-column gap-1">
                      <div>
                        <b>{T("word.from")}:</b> {fmtDatetime(first?.key, tz.display)}
                      </div>
                      <div>
                        <b>{T("word.to")}:</b> {fmtDatetime(last?.key, tz.display)}
                      </div>
                      <div>
                        <b>{T("action.count")}:</b> {count}
                      </div>
                    </div>
                  ) : (
                    T("word.no-data")
                  )}
                </Tooltip>
              </ValueContainer>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const lastValueTemplateForWeekYearComparison = () => {
    if (!Array.isArray(dataRange) || dataRange.length !== 1 || props.dataDisplayType !== DataDisplayType.WEEK_YEAR_COMPARISON) {
      return null;
    }

    const dataset = dataRange[0];

    if (props.useDataUpToLastFinishedMonth && dataset.data[1].key) {
      const isInLastMonth = checkIfDataInLastFinishedWeekExists(dataset.data[1].key, tz.company);
      if (!isInLastMonth) return noDataInLastFinishedWeekTemplate();
    }

    const comparison = getWeekYearComparison(dataset, props.useDataUpToLastFinishedWeek ?? false);
    if (!comparison || comparison.current == null) {
      return (
        <div style={{ color: getTextColor(props.theme) }} className="font-xl">
          {getNoDataMessageWeek(props.useDataUpToLastFinishedWeek)}
        </div>
      );
    }

    const value = comparison.current;
    const { refWeek, refYear } = comparison;

    const point = dataset.data.find(p => {
      const zdt = Instant.parse(p.key).atZone(tz.company);
      return zdt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR) === refWeek && zdt.year() === refYear;
    });

    const safeId = safeIdString(dataset.identifier);
    const tooltipId = `last-value-weekyear-${safeId}-tooltip`;

    const week = getCurrentDate(tz.display, true, props.useDataUpToLastFinishedWeek);
    const year = point ? Instant.parse(point.key).atZone(tz.company).year() : refYear;
    if (props.useHorizontalBarChart) {
      return horizonatlBarChartTemplate();
    }

    return (
      <ValueContainer
        id={tooltipId}
        value={formatNumber(value, decimalPlaces, props.convertToAbsoluteValues)}
        unit={!props.hideUnit ? dataset.unit : undefined}
        highlight={props.highlightValue}
        color={getTextColor(props.theme)}
        onDoubleClick={() => setDialog(true)}
      >
        <Tooltip target={`#${tooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
          {point ? (
            <div>
              {T("word.current-value")}: {value.toFixed(props.diffValueDecimalPlaces)}, {year} {week}
            </div>
          ) : (
            T("word.no-data")
          )}
        </Tooltip>
      </ValueContainer>
    );
  };

  const flexDirectionClass = props.displayInline ? "flex-row justify-content-between" : "flex-column";

  const className = `${getMainClassName(props.dataDisplayType, props.reportMode, props.displayInline)} flex ${flexDirectionClass}`;
  return (
    <div style={{ width: getComponentWidth() }} className={className}>
      <div className="flex flex-row justify-content-between">
        {titleAndSubtitleTemplate()}
        {diffValueForLastValue()}
        {diffValueForMonthYearComparison()}
        {diffValueForWeekYearComparison()}
      </div>

      {lastValueTemplate()}
      {lastValueTemplateForMonthYearComparison()}
      {lastValueTemplateForAverage()}
      {lastValueTemplateForWeekYearComparison()}

      {props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART &&
        dataRange &&
        props.aggregationPeriod &&
        horizonatlBarChartTemplate()}

      {data &&
        data?.length > 1 &&
        !props.displayLastValueDiff &&
        props.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART &&
        props.dataDisplayType !== DataDisplayType.AVERAGE_VALUE && (
          <div className={`value-column ${props.displayInline ? "inline-values" : "column-values mt-2"} `}>
            {props.hideIfValueIsZeroOrMore && data[0].data[0].value && data[0].data[0].value >= 0
              ? null
              : data.map(renderDatasetValue)}
          </div>
        )}

      <Dialog
        header={T("label.scada-tags-chart")}
        visible={dialog}
        style={{ width: "min(100vw, 1000px)", height: "500px" }}
        modal={false}
        onHide={() => setDialog(false)}
      >
        <DatasetLineChart title="" identifiers={props.datasets} showDateSelection dynamicHeight chartDialogOpen={dialog} />
      </Dialog>
    </div>
  );
}
